import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {FormattedMessage, useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {
	Announcements,
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
	Button,
	EMPHASIS,
	Icon,
	IconTooltip,
	Link,
	OutlinedPillVariant,
	Pill,
	Text,
	Tooltip,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import SettingsProjectSubNav from './SettingsProjectSubNav';
import {isLastWithProfile, stageLabel, StageProfile} from '../../shared/util/projectStageUtil';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';
import {useForecastFetchQuery} from '../../shared/hooks/useForecastFetchQuery';
import {showProjectStageModal} from './modals/ProjectStageModal';
import {showDeleteProjectStageModal} from './modals/DeleteProjectStageModal';
import {useProjectStageOrder} from './useProjectStageOrder';
import type {SettingsProjectStages_viewer$key} from './__generated__/SettingsProjectStages_viewer.graphql';
import type {SettingsProjectStagesQuery$data} from './__generated__/SettingsProjectStagesQuery.graphql';

const Page = Styled.div`
	max-width: 1000px;
	margin-left: 24px;
	margin-top: 6px;
	font-size: 13px;
`;

const StageList = Styled.ol`
	list-style: none;
	padding: 0;
	margin: 0 0 12px;
	max-width: 620px;
`;

const PILL_COLUMN_WIDTH = '128px';
const COUNT_COLUMN_WIDTH = '64px';
const ACTIONS_COLUMN_WIDTH = '80px';
const COLUMN_GAP = '16px';
const HELP_CENTER_URL = 'https://support.forecast.app/hc/en-us/articles/51155007113233-Using-Project-Stages';

const Intro = Styled.div`
	max-width: 740px;
	margin-bottom: 16px;
`;

const ListHeader = Styled.div`
	display: flex;
	align-items: center;
	gap: ${COLUMN_GAP};
	max-width: 620px;
	padding: 0 12px 8px;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;

const HeaderCell = Styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: ${tokensForThemeProvider.sky.sys.color.text.content.auxiliary};
`;

const StageHeader = Styled(HeaderCell)`
	flex: 1;
`;

const PillHeader = Styled(HeaderCell)`
	width: ${PILL_COLUMN_WIDTH};
	justify-content: center;
`;

const CountHeader = Styled(HeaderCell)`
	width: ${COUNT_COLUMN_WIDTH};
`;

const ActionsSpacer = Styled.div`
	width: ${ACTIONS_COLUMN_WIDTH};
`;

const PillCell = Styled.div`
	width: ${PILL_COLUMN_WIDTH};
	display: flex;
	justify-content: center;
	& > * {
		flex: 1;
		justify-content: center;
	}
`;

const CountCell = Styled.div`
	width: ${COUNT_COLUMN_WIDTH};
`;

const AddRow = Styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const RowEnd = Styled.div`
	display: flex;
	align-items: center;
	gap: ${COLUMN_GAP};
`;

const StageRow = Styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 40px;
	padding: 0 12px;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;

const StageLabel = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const RowActions = Styled.div`
	display: flex;
	align-items: center;
	width: ${ACTIONS_COLUMN_WIDTH};
	justify-content: flex-end;
`;

const DragHandle = Styled.button`
	display: flex;
	align-items: center;
	padding: 4px;
	border: none;
	background: none;
	cursor: grab;
	&[aria-disabled='true'] {
		cursor: default;
		opacity: 0.4;
	}
`;

// Names the icon-only edit button: the design system's Button overwrites aria-label, so the name comes
// from this visually hidden element through aria-labelledby.
const AssistiveText = Styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	white-space: nowrap;
	clip: rect(0 0 0 0);
	clip-path: inset(50%);
`;

export type ProjectStageRow = {
	id: string;
	name: string | null;
	profile?: string | null;
	sortOrder: number | null;
	projectCount?: number | null;
};

const SortableStageRow = ({
	id,
	label,
	handleLabel,
	disabled,
	children,
}: {
	id: string;
	label: string;
	handleLabel: string;
	disabled: boolean;
	children?: React.ReactNode;
}): JSX.Element => {
	const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging} = useSortable({
		id,
		disabled,
	});
	return (
		<StageRow
			ref={setNodeRef}
			style={{transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1}}
			data-cy={'project-stage-row'}
		>
			<StageLabel>
				<DragHandle
					type="button"
					ref={setActivatorNodeRef}
					{...attributes}
					{...listeners}
					aria-label={handleLabel}
					// aria-disabled rather than disabled: a natively disabled handle drops keyboard focus
					// to <body> after a drop. useSortable({disabled}) already stops drags while saving.
					aria-disabled={disabled}
					data-cy={'project-stage-drag-handle'}
				>
					<Icon icon={'drag'} size={'m'} />
				</DragHandle>
				<span data-cy={'project-stage-name'}>{label}</span>
			</StageLabel>
			{children}
		</StageRow>
	);
};

export const SettingsProjectStagesComponent = ({
	companyId,
	stages,
	refetchStages = () => Promise.resolve(),
}: {
	companyId: string;
	stages: ReadonlyArray<ProjectStageRow>;
	refetchStages?: () => Promise<unknown>;
}): JSX.Element => {
	const intl = useIntl();
	const {ordered, saving, moveStage} = useProjectStageOrder(stages, refetchStages);
	const editLabel = intl.formatMessage({id: 'settings.project_stages.edit'});
	const deleteLabel = intl.formatMessage({id: 'settings.project_stages.delete'});

	// Default stage names are translated when a translation exists; any other name is shown as stored.
	const label = (stage: ProjectStageRow) => stageLabel(stage, intl);

	// The duplicate check runs against stored names, so the defaults are compared by their English names
	// exactly as the server compares them.
	const namesExcept = (excludedId?: string) =>
		ordered.filter(stage => stage.id !== excludedId).map(stage => stage.name ?? '');

	const handleAdd = () => showProjectStageModal({companyId, otherNames: namesExcept()});

	const handleEdit = (stage: ProjectStageRow) =>
		showProjectStageModal({
			companyId,
			stage: {id: stage.id, name: stage.name ?? '', profile: (stage.profile ?? 'PLANNING') as StageProfile},
			lastWithProfile: isLastWithProfile(stage, ordered),
			otherNames: namesExcept(stage.id),
		});

	const handleDelete = (stage: ProjectStageRow) =>
		showDeleteProjectStageModal({
			companyId,
			stage: {id: stage.id, label: label(stage), profile: (stage.profile ?? null) as StageProfile | null},
			otherStages: ordered
				.filter(other => other.id !== stage.id)
				.map(other => ({id: other.id, label: label(other), profile: (other.profile ?? null) as StageProfile | null})),
			projectCount: stage.projectCount ?? 0,
			lastWithProfile: isLastWithProfile(stage, ordered),
			lastStage: ordered.length === 1,
		});

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	const labelOf = (id: string | number) => {
		const stage = ordered.find(candidate => candidate.id === id);
		return stage ? label(stage) : '';
	};
	const positionOf = (id: string | number) => ordered.findIndex(candidate => candidate.id === id) + 1;

	const announcements: Announcements = {
		onDragStart: ({active}) =>
			intl.formatMessage({id: 'settings.project_stages.drag.picked_up'}, {name: labelOf(active.id)}),
		onDragOver: ({active, over}) =>
			over
				? intl.formatMessage(
						{id: 'settings.project_stages.drag.moved'},
						{name: labelOf(active.id), position: positionOf(over.id), total: ordered.length}
				  )
				: undefined,
		onDragEnd: ({active, over}) =>
			over
				? intl.formatMessage(
						{id: 'settings.project_stages.drag.dropped'},
						{name: labelOf(active.id), position: positionOf(over.id), total: ordered.length}
				  )
				: undefined,
		onDragCancel: ({active}) =>
			intl.formatMessage({id: 'settings.project_stages.drag.cancelled'}, {name: labelOf(active.id)}),
	};

	const handleDragEnd = ({active, over}: DragEndEvent) => {
		if (over) {
			moveStage(String(active.id), String(over.id));
		}
	};

	return (
		<div className="section-content settings-app" data-cy={'settings-project-stages-page'}>
			<ForecastThemeProvider>
				<>
					<SettingsProjectSubNav />
					<Page>
						<Intro>
							<Text>
								{intl.formatMessage({id: 'settings.project_stages.intro'})}{' '}
								<FormattedMessage
									id="settings.project_stages.intro.help_center"
									values={{
										a: (chunks: React.ReactNode) => (
											<Link href={HELP_CENTER_URL} target={'_blank'} rel="noopener noreferrer">
												{chunks}
											</Link>
										),
									}}
								/>
							</Text>
						</Intro>
						<ListHeader>
							<StageHeader>{intl.formatMessage({id: 'settings.project_stages.list.stage'})}</StageHeader>
							<PillHeader>
								{intl.formatMessage({id: 'settings.project_stages.behaves_like'})}
								<IconTooltip icon={'info'}>
									<IconTooltip.Item
										title={intl.formatMessage({id: 'settings.project_stages.behaves_like'})}
										details={intl.formatMessage({id: 'settings.project_stages.behaves_like.tooltip'})}
									/>
								</IconTooltip>
							</PillHeader>
							<CountHeader>{intl.formatMessage({id: 'settings.project_stages.list.projects'})}</CountHeader>
							<ActionsSpacer />
						</ListHeader>
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							accessibility={{
								announcements,
								screenReaderInstructions: {
									draggable: intl.formatMessage({id: 'settings.project_stages.drag.instructions'}),
								},
							}}
						>
							<SortableContext items={ordered.map(stage => stage.id)} strategy={verticalListSortingStrategy}>
								<StageList data-cy={'project-stages-list'}>
									{ordered.map(stage => {
										const editLabelId = `project-stage-edit-label-${stage.id}`;
										const deleteLabelId = `project-stage-delete-label-${stage.id}`;
										return (
											<SortableStageRow
												key={stage.id}
												id={stage.id}
												label={label(stage)}
												handleLabel={intl.formatMessage(
													{id: 'settings.project_stages.reorder_handle'},
													{name: label(stage)}
												)}
												disabled={saving}
											>
												<RowEnd>
													<PillCell data-cy={'project-stage-profile-pill'}>
														{stage.profile ? (
															<Pill outlined outlinedVariant={OutlinedPillVariant.NEUTRAL}>
																{intl.formatMessage({
																	id: `project_status.${stage.profile.toLowerCase()}`,
																})}
															</Pill>
														) : null}
													</PillCell>
													<CountCell data-cy={'project-stage-project-count'}>
														<Text>{stage.projectCount ?? 0}</Text>
													</CountCell>
													<RowActions>
														<AssistiveText id={editLabelId}>{editLabel}</AssistiveText>
														<Tooltip content={editLabel} interactable={false}>
															<Button
																emphasis={EMPHASIS.LOW}
																icon={'edit'}
																iconOnly
																onPress={() => handleEdit(stage)}
																aria-labelledby={editLabelId}
																data-cy={'project-stage-edit'}
															/>
														</Tooltip>
														<AssistiveText id={deleteLabelId}>{deleteLabel}</AssistiveText>
														<Tooltip content={deleteLabel} interactable={false}>
															<Button
																emphasis={EMPHASIS.LOW}
																icon={'trash'}
																iconOnly
																onPress={() => handleDelete(stage)}
																aria-labelledby={deleteLabelId}
																data-cy={'project-stage-delete'}
															/>
														</Tooltip>
													</RowActions>
												</RowEnd>
											</SortableStageRow>
										);
									})}
								</StageList>
							</SortableContext>
						</DndContext>
						<AddRow>
							<Button emphasis={EMPHASIS.LOW} icon={'plus'} onPress={handleAdd} data-cy={'project-stages-add'}>
								{intl.formatMessage({id: 'settings.project_stages.add'})}
							</Button>
						</AddRow>
					</Page>
				</>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsProjectStagesQuery = graphql`
	query SettingsProjectStagesQuery {
		viewer {
			actualPersonId
			component(name: "settings_project_stages")
			...SettingsProjectStages_viewer
		}
	}
`;

const VIEWER_FRAGMENT = graphql`
	fragment SettingsProjectStages_viewer on Viewer {
		company {
			id
			projectStages(first: 1000) @connection(key: "Company_projectStages") {
				edges {
					node {
						id
						name
						sortOrder
						profile
						projectCount
					}
				}
			}
		}
	}
`;

const SettingsProjectStages = ({viewer}: {viewer: SettingsProjectStages_viewer$key}): JSX.Element => {
	const data = useFragment(VIEWER_FRAGMENT, viewer);
	const {fetch} = useForecastFetchQuery<SettingsProjectStagesQuery$data, Record<string, never>>(SettingsProjectStagesQuery);
	const refetchStages = () => new Promise((resolve, reject) => fetch({}, resolve, reject));
	const stages = (data.company?.projectStages?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
	return <SettingsProjectStagesComponent companyId={data.company?.id ?? ''} stages={stages} refetchStages={refetchStages} />;
};

export default SettingsProjectStages;
